import { type MouseEvent, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  TableBreakdownStack,
  TableMetaInlineValueRow,
  TableMetaStack,
} from '@/features/shared/components/tableSystem/TableMetaCellBase';
import { r, s } from '@/theme/tokens';
import { Chip, Link, type Theme } from '@/ui';

type TableStatusMetaContentProps = {
  statusLabel: string;
  statusHref?: string | null;
  onStatusClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  chipTone: React.ComponentProps<typeof Chip>['tone'];
  chipIcon: ReactElement;
  chipBackgroundColor: (theme: Theme) => string;
  chipForegroundColor: (theme: Theme) => string;
  durationLabel: string;
  durationIcon: ReactElement;
  durationTitle?: string;
  durationValueTitle?: string | null;
  timestampLabel: string;
  timestampTitle?: string | null;
  timestampIcon: ReactElement;
  timestampRowTitle?: string;
  compactTopSpacing?: boolean;
};

export const STATUS_META_TIMESTAMP_CONTENT_SX = {
  overflow: 'visible',
} as const;

export const STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX = {
  display: 'inline-block',
  paddingBottom: '1px',
} as const;

const STATUS_META_CHIP_FONT_SIZE = '14px !important';

export const STATUS_META_CHIP_LINK_SX = {
  color: 'inherit',
  fontWeight: 'inherit',
  fontSize: STATUS_META_CHIP_FONT_SIZE,
  textDecoration: 'none',
  textDecorationColor: 'inherit',
  '&:hover, &:focus-visible, &:active': {
    color: 'inherit',
    textDecoration: 'underline',
    textDecorationColor: 'inherit',
  },
  '&:visited': {
    color: 'inherit',
  },
} as const;

export const TableStatusMetaContent = ({
  statusLabel,
  statusHref = null,
  onStatusClick,
  chipTone,
  chipIcon,
  chipBackgroundColor,
  chipForegroundColor,
  durationLabel,
  durationIcon,
  durationTitle = 'Duration',
  durationValueTitle,
  timestampLabel,
  timestampTitle,
  timestampIcon,
  timestampRowTitle = 'Executed',
  compactTopSpacing: _compactTopSpacing = false,
}: TableStatusMetaContentProps): ReactElement => {
  const hasDurationLabel = durationLabel.length > 0;
  const hasTimestampLabel = timestampLabel.length > 0;

  return (
    <TableMetaStack
      rowGap={s('none')}
      sx={{
        minHeight: !hasDurationLabel && hasTimestampLabel ? '58px' : undefined,
      }}
    >
      <Chip
        size="xs"
        icon={chipIcon}
        label={
          statusHref ? (
            <Link
              component={RouterLink}
              to={statusHref}
              underline="hover"
              color="inherit"
              onClick={onStatusClick}
            >
              {statusLabel}
            </Link>
          ) : (
            statusLabel
          )
        }
        tone={chipTone}
        sx={{
          mt: s('xs'),
          mb: `calc(${s('sm')} + ${s('xs')})`,
          width: 'fit-content',
          maxWidth: '100%',
          alignSelf: 'flex-start',
          borderRadius: r('pill'),
          backgroundColor: chipBackgroundColor,
          color: chipForegroundColor,
          '&:hover, &.Mui-focusVisible, &:active': {
            backgroundColor: chipBackgroundColor,
            color: chipForegroundColor,
          },
          '& .MuiChip-label': {
            color: 'inherit',
            fontWeight: 500,
            fontSize: STATUS_META_CHIP_FONT_SIZE,
          },
          '& .MuiChip-label .MuiTypography-root': {
            color: 'inherit',
            fontWeight: 'inherit',
            fontSize: STATUS_META_CHIP_FONT_SIZE,
          },
          '& .MuiChip-label a': STATUS_META_CHIP_LINK_SX,
          '& .MuiChip-label *': {
            fontSize: STATUS_META_CHIP_FONT_SIZE,
          },
          '& .MuiChip-icon': {
            color: 'inherit',
            fontSize: (theme) => theme.app.iconSizes.sm,
            marginRight: '-6px',
          },
        }}
      />
      {hasDurationLabel || hasTimestampLabel ? (
        <TableBreakdownStack>
          {hasDurationLabel ? (
            <TableMetaInlineValueRow
              title={durationTitle}
              icon={durationIcon}
              content={durationLabel}
              valueTitle={durationValueTitle}
            />
          ) : null}
          {hasTimestampLabel ? (
            <TableMetaInlineValueRow
              title={timestampRowTitle}
              icon={timestampIcon}
              content={timestampLabel}
              valueTitle={timestampTitle}
              contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
              valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
            />
          ) : null}
        </TableBreakdownStack>
      ) : null}
    </TableMetaStack>
  );
};
